/**
 * CostBreakdownChart Component
 *
 * Displays AI blog generation cost breakdown by phase as horizontal bars.
 * Uses simple CSS-based bars with Tailwind, no external charting library required.
 *
 * Story: BLOG-AI-004, AC-6, AC-9
 */
import type { CostByPhase } from '@/types/blog.types'

interface CostBreakdownChartProps {
  data: CostByPhase
}

export function CostBreakdownChart({ data }: CostBreakdownChartProps) {
  // Calculate total for percentage calculations
  const total = Object.values(data).reduce((sum, value) => sum + value, 0)

  // Phase configuration with labels and colors
  const phases = [
    {
      key: 'research_summary' as keyof CostByPhase,
      label: 'Research Summary',
      color: 'bg-blue-600 dark:bg-blue-700',
    },
    {
      key: 'outline' as keyof CostByPhase,
      label: 'Outline Generation',
      color: 'bg-purple-600 dark:bg-purple-700',
    },
    {
      key: 'content_generation' as keyof CostByPhase,
      label: 'Content Writing',
      color: 'bg-orange-600 dark:bg-orange-700',
    },
    {
      key: 'seo_optimization' as keyof CostByPhase,
      label: 'SEO Optimization',
      color: 'bg-green-600 dark:bg-green-700',
    },
  ]

  // Handle zero total case
  if (total === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground text-sm">
        No cost data available
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {phases.map((phase) => {
        const cost = data[phase.key]
        const percentage = (cost / total) * 100

        return (
          <div key={phase.key} className="space-y-1">
            {/* Phase label and cost */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-foreground font-medium">{phase.label}</span>
              <span className="text-muted-foreground">
                ${cost.toFixed(2)} ({percentage.toFixed(1)}%)
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${phase.color} transition-all duration-300`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}
